import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react'
import { Button } from '@/components/ui/button'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  setInput,
  setActiveAdvisor,
  setActiveProfessor,
  updateAdvisorMessage,
  setAdvisorDraft,
  setNegotiationDraft,
} from '@/redux/slices/chatSlice'
import {
  sendAdvisorWebSocketMessage,
  stopResponseGeneration,
} from '@/redux/asyncThunks/websocketThunks'
import { sendNegotiationMessage } from '@/redux/asyncThunks/negotiationWebsocket'
import { clearReplyTarget } from '@/redux/slices/negotiationWebsocketSlice'
import { X } from 'lucide-react'
import { sendTeamChatMessage } from '@/redux/asyncThunks/teamChat'
import { sendAIPlayerMessage } from '@/redux/asyncThunks/aiPlayerChat'
import { setError as setAIPlayerError } from '@/redux/slices/aiPlayerChatSlice'
import { ChatMessage } from '@/redux/types/chat'
import { ActiveTab, isAIPlayerTab, SenderType } from '@/constants/chat'
import { sendAdjudicationMessage } from '@/redux/asyncThunks/adjudicationMessageWebsocket'
import { UserRole } from '@/constants/room'
import { useGamePhase } from '@/hooks/useGamePhase'
import { Textarea } from '@/components/ui/textarea'
import { shallowEqual, useSelector } from 'react-redux'
import { RootState } from '@/redux/store'
import { ChatbotType } from '@/redux/types/configuration'
import { useEffectiveUser } from '@/hooks/useEffectiveUser'
import { useTeamCommunicator } from '@/hooks/useTeamCommunicator'
import { toast } from 'sonner'

type ChatPillProps = {
  showActionButtons: boolean
  disabled?: boolean
}

const ChatPill: React.FC<ChatPillProps> = React.memo(
  ({ showActionButtons, disabled = false }) => {
    const dispatch = useAppDispatch()
    const [textInput, setTextInput] = useState('')
    const inputRef = useRef<HTMLTextAreaElement>(null)
    const { teamChatbots } = useAppSelector((state) => state.teamChatbot)

    const { user } = useEffectiveUser()
    const { shouldShowChatPill } = useTeamCommunicator()
    const advisors = useAppSelector(
      (state) => state.advisor.advisors,
      shallowEqual
    )
    const activeAdvisors = useAppSelector(
      (state) => state.chat.activeAdvisor,
      shallowEqual
    )
    const activeProfessor = useAppSelector(
      (state) => state.chat.activeProfessor,
      shallowEqual
    )
    const input = useAppSelector((state) => state.chat.input, shallowEqual)
    const activeTab = useAppSelector(
      (state) => state.chat.activeTab,
      shallowEqual
    )
    const { phaseId, manualMode, advisorPhase, decisionPhase } = useGamePhase()

    const randrChatbot = useMemo(
      () =>
        advisors.find((advisor) => advisor.chatbottype === ChatbotType.RANDR),
      [advisors]
    )

    const isGenerating = useSelector(
      (state: RootState) => state.chat.isGenerating,
      shallowEqual
    )

    // Set only for negotiation tabs, so it is inert on advisor/team/AI tabs.
    const replyTarget = useSelector(
      (state: RootState) =>
        state.negotiationWS.replyTargets[String(activeTab)],
      shallowEqual
    )

    const isProfessor = user?.role === UserRole.Professor

    const isTextareaDisabled = useMemo(
      () =>
        disabled ||
        (activeAdvisors.length === 0 &&
          !activeProfessor &&
          !isProfessor &&
          activeTab === ActiveTab.Advisor),
      [disabled, activeAdvisors.length, activeProfessor, isProfessor, activeTab]
    )

    useEffect(() => {
      setTextInput(input)
    }, [input])

    const handleInputChange = useCallback(
      (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value
        setTextInput(value)
        dispatch(setInput(value))

        if (activeTab === ActiveTab.Adjudication) {
          return
        }

        if (activeTab === ActiveTab.Advisor) {
          dispatch(setAdvisorDraft(value))
        } else if (activeTab === ActiveTab.TeamRoom) {
        } else if (isAIPlayerTab(activeTab)) {
        } else {
          dispatch(
            setNegotiationDraft({
              negotiationId: activeTab,
              draft: value,
            })
          )
        }
      },
      [activeTab, dispatch]
    )

    const handleInputKeyDown = useCallback(
      (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && e.shiftKey) {
          e.preventDefault()
          const textarea = e.target as HTMLTextAreaElement
          const start = textarea.selectionStart
          const end = textarea.selectionEnd
          const value = textarea.value

          const newValue =
            value.substring(0, start) + '\n' + value.substring(end)
          setTextInput(newValue)
          dispatch(setInput(newValue))

          if (activeTab === ActiveTab.Adjudication) {
            return
          }
          if (activeTab === ActiveTab.Advisor) {
            dispatch(setAdvisorDraft(newValue))
          } else if (activeTab === ActiveTab.TeamRoom) {
          } else if (isAIPlayerTab(activeTab)) {
          } else {
            dispatch(
              setNegotiationDraft({
                negotiationId: activeTab,
                draft: newValue,
              })
            )
          }

          setTimeout(() => {
            textarea.selectionStart = textarea.selectionEnd = start + 1
          }, 0)

          return
        }

        if (e.key === 'Enter' && !e.shiftKey) {
          e.preventDefault()
          handleSendMessage(e as any)
          return
        }
      },
      [textInput, activeTab, dispatch]
    )

    const handleSendMessage = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        if (!textInput.trim()) return

        if (activeTab === ActiveTab.Advisor) {
          const currentPhaseId = manualMode ? advisorPhase?.id : phaseId

          // if professor is sending message
          if (isProfessor && currentPhaseId != null) {
            const advisorMessage: ChatMessage = {
              id: Date.now().toString(),
              message: textInput.trim(),
              user: user?.email || 'User',
              sender_type: SenderType.Professor,
              sendToProfessor: false,
              phaseId: currentPhaseId,
              created_at: new Date().toISOString(),
            }
            dispatch(updateAdvisorMessage(advisorMessage))
            dispatch(
              sendAdvisorWebSocketMessage({
                message: advisorMessage,
                chatbotId: ['null'],
              })
            )
          } else {
            // player is sending msg to assistant
            if (currentPhaseId == null) {
              toast.error(
                'Unable to send: game phase not yet loaded. Please try again in a moment.'
              )
              return
            }

            if (
              activeAdvisors.length === 0 &&
              !activeProfessor &&
              randrChatbot
            ) {
              const advisorMessage: ChatMessage = {
                id: Date.now().toString(),
                message: textInput.trim(),
                user: user?.email || 'User',
                sender_type: SenderType.Player,
                sendToProfessor: false,
                phaseId: currentPhaseId,
                created_at: new Date().toISOString(),
              }
              dispatch(updateAdvisorMessage(advisorMessage))
              dispatch(
                sendAdvisorWebSocketMessage({
                  message: advisorMessage,
                  chatbotId: [randrChatbot.chatbotid],
                })
              )
            } else if (activeAdvisors.length === 0 && activeProfessor) {
              const advisorMessage: ChatMessage = {
                id: Date.now().toString(),
                message: `@white-cell ${textInput.trim()}`,
                user: user?.email || 'User',
                sender_type: SenderType.Player,
                sendToProfessor: true,
                phaseId: currentPhaseId,
                created_at: new Date().toISOString(),
              }
              dispatch(updateAdvisorMessage(advisorMessage))
              dispatch(
                sendAdvisorWebSocketMessage({
                  message: advisorMessage,
                  chatbotId: ['null'],
                })
              )
            } else {
              // player is sending msg to multiple advisors
              const groupId =
                activeAdvisors.length > 1 ? `group-${Date.now()}` : undefined

              // Build combined message with all advisor names
              const advisorNames = activeAdvisors
                .map((advisorId) => {
                  const advisorName =
                    advisors.find((a) => a.chatbotid === advisorId)
                      ?.chatbotname ||
                    teamChatbots.find((tc) => tc.chatbot === advisorId)
                      ?.chatbot_name ||
                    ''
                  return advisorName
                })
                .filter((name) => name !== '')

              const combinedMessage =
                advisorNames.map((name) => `@${name}`).join(' ') +
                ' ' +
                textInput.trim()

              const advisorMessage: ChatMessage = {
                id: Date.now().toString(),
                message: combinedMessage,
                user: user?.email || 'User',
                sender_type: SenderType.Player,
                sendToProfessor: false,
                phaseId: currentPhaseId,
                created_at: new Date().toISOString(),
                multiAdvisorGroupId: groupId,
              }
              dispatch(updateAdvisorMessage(advisorMessage))

              // Send WebSocket message with raw text and chatbot IDs
              const wsMessage: ChatMessage = {
                id: Date.now().toString(),
                message: textInput.trim(),
                user: user?.email || 'User',
                sender_type: SenderType.Player,
                sendToProfessor: false,
                phaseId: currentPhaseId,
                created_at: new Date().toISOString(),
                multiAdvisorGroupId: groupId,
              }
              dispatch(
                sendAdvisorWebSocketMessage({
                  message: wsMessage,
                  chatbotId: activeAdvisors,
                })
              )
            }
          }
        } else if (activeTab === ActiveTab.Adjudication) {
          const currentPhaseId = manualMode ? decisionPhase?.id : phaseId

          if (currentPhaseId == null) {
            console.error('Phase ID is required but not available')
            return
          }

          const newMessage: ChatMessage = {
            id: Date.now().toString(),
            message: textInput.trim(),
            user: user?.email || 'User',
            phaseId: currentPhaseId,
          }
          dispatch(
            sendAdjudicationMessage({
              message: newMessage.message,
              senderName: user?.email || 'User',
            })
          )
        } else if (activeTab === ActiveTab.TeamRoom) {
          dispatch(
            sendTeamChatMessage({
              message: textInput.trim(),
            })
          )
        } else if (isAIPlayerTab(activeTab)) {
          dispatch(setAIPlayerError(null))
          void dispatch(
            sendAIPlayerMessage({
              message: textInput.trim(),
              idempotencyKey: `${Date.now()}-${user?.id || 'user'}`,
            })
          )
            .unwrap()
            .catch((err: unknown) => {
              toast.error(
                typeof err === 'string'
                  ? err
                  : 'Could not send to AI Player. Check connection and try again.'
              )
            })
        } else {
          const negotiationId = activeTab

          dispatch(
            sendNegotiationMessage({
              negotiationId,
              message: textInput.trim(),
              replyTo: replyTarget?.id,
            })
          )
          dispatch(
            setNegotiationDraft({ negotiationId: negotiationId, draft: '' })
          )
          if (replyTarget) {
            dispatch(clearReplyTarget(negotiationId))
          }
        }
        dispatch(setInput(''))
        dispatch(setAdvisorDraft(''))
        dispatch(setActiveProfessor(false))
        dispatch(setActiveAdvisor([]))

        inputRef.current?.focus()
      },
      [
        textInput,
        activeTab,
        manualMode,
        advisorPhase?.id,
        phaseId,
        isProfessor,
        user?.email,
        dispatch,
        activeAdvisors,
        activeProfessor,
        randrChatbot,
        advisors,
        decisionPhase?.id,
        replyTarget,
      ]
    )

    const handleStopStreaming = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        dispatch(stopResponseGeneration())
      },
      [dispatch]
    )

    if (!shouldShowChatPill && !isProfessor) {
      return null
    }

    return (
      <div className='flex flex-col gap-2'>
        {replyTarget && (
          <div className='flex items-center justify-between rounded-lg border border-[#CBD5E1] bg-white px-3 py-1.5'>
            <div className='min-w-0 border-l-2 border-dark-blue pl-2'>
              <p className='text-xs font-semibold text-dark-blue'>
                Replying to {replyTarget.name}
              </p>
              <p className='truncate text-xs text-gray-600'>
                {replyTarget.excerpt}
              </p>
            </div>
            <button
              type='button'
              aria-label='Cancel reply'
              onClick={() => dispatch(clearReplyTarget(String(activeTab)))}
              className='ml-2 flex-shrink-0 rounded-full p-1 text-gray-400 hover:bg-slate-100 hover:text-gray-600'
            >
              <X className='h-4 w-4' />
            </button>
          </div>
        )}
        <form onSubmit={handleSendMessage} className='flex flex-1 gap-2'>
          <div className='relative min-h-[50px] flex items-start flex-1 px-3 py-2 border border-[#CBD5E1] bg-[#E6ECF2] rounded-2xl'>
            <div className='flex flex-wrap items-center gap-1 w-full'>
              {/* Text Input */}
              <Textarea
                ref={inputRef}
                value={textInput}
                disabled={isTextareaDisabled}
                onChange={handleInputChange}
                onKeyDown={handleInputKeyDown}
                placeholder={
                  isTextareaDisabled
                    ? 'Choose an advisor(s) or whitecell to begin typing your message'
                    : 'Type your message...'
                }
                rows={1}
                className='flex-1 min-w-[100px] min-h-[36px] max-h-40 resize-none overflow-y-auto text-sm  leading-[36px] bg-transparent border-none shadow-none focus:outline-none focus:ring-0 focus:border-none focus:shadow-none focus-visible:outline-none focus-visible:ring-0 appearance-none'
              />
            </div>

            {/* Send/Stop Button */}
            <div className='flex items-center ml-2'>
              {isGenerating &&
              activeTab === ActiveTab.Advisor &&
              !isProfessor ? (
                <Button
                  className='bg-transparent shadow-none hover:bg-transparent p-1'
                  onClick={handleStopStreaming}
                  type='button'
                  size='icon'
                >
                  <img
                    src={'/icons/stop-circle.svg'}
                    alt='Stop'
                    className='h-7 w-7'
                  />
                </Button>
              ) : (
                <Button
                  className='bg-transparent shadow-none hover:bg-transparent p-1'
                  disabled={
                    !textInput.trim() ||
                    disabled ||
                    (activeTab === ActiveTab.Advisor &&
                      activeAdvisors.length === 0 &&
                      activeProfessor === false &&
                      user?.role === UserRole.Student)
                  }
                  type='submit'
                  size='icon'
                >
                  <img
                    src={
                      !textInput.trim()
                        ? '/icons/arrow-up-circle-white.svg'
                        : '/icons/arrow-up-circle.svg'
                    }
                    alt='Send'
                    className='h-7 w-7'
                  />
                </Button>
              )}
            </div>
          </div>
        </form>
        {showActionButtons && (
          <div className='flex gap-3'>
            <Button className='px-4 py-2 bg-dark-blue hover:bg-dark-blue-hovered disabled:bg-[#CED2D8] text-white text-base disabled:text-[#6D6E71]'>
              Send Adjudication
            </Button>
            <Button className='px-4 py-2 bg-dark-blue hover:bg-dark-blue-hovered disabled:bg-[#CED2D8] text-white text-base disabled:text-[#6D6E71]'>
              Update Scenario
            </Button>
            <Button className='px-4 py-2 bg-[#EE183C] hover:bg-red-700 disabled:bg-[#CED2D8] text-white text-base disabled:text-[#6D6E71]'>
              Exit
            </Button>
          </div>
        )}
      </div>
    )
  }
)

ChatPill.displayName = 'ChatPill'

export default ChatPill
